import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save, Loader2 } from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { PageHeader } from '../../components/ui';
import { Panel, Button, buttonClass } from '../../design-system';

const emptyToNull = z.preprocess(
  (val) => (val === '' || val === 0 || val === '0' || Number.isNaN(val) ? null : val),
  z.coerce.number().nullable().optional()
);

const tarefaSchema = z.object({
  titulo: z.string().min(1, 'Título é obrigatório'),
  descricao: z.string().optional(),
  tipo: z.string().optional(),
  prioridade: z.enum(['URGENTE', 'ALTA', 'MEDIA', 'BAIXA']).default('MEDIA'),
  dataVencimento: z.string().min(1, 'Data de vencimento é obrigatória'),
  usuarioId: emptyToNull,
  leadId: emptyToNull,
  veiculoId: emptyToNull,
  concluida: z.boolean().default(false),
});

type TarefaFormData = z.infer<typeof tarefaSchema>;

interface Vendedor {
  id: number;
  nome: string;
}

interface LeadOption {
  id: number;
  nome: string;
}

interface VeiculoOption {
  id: number;
  descricao: string;
  placa: string;
}

const tiposOptions = [
  { value: 'LIGACAO', label: 'Ligação' },
  { value: 'EMAIL', label: 'E-mail' },
  { value: 'REUNIAO', label: 'Reunião' },
  { value: 'VISITA', label: 'Visita' },
  { value: 'TEST_DRIVE', label: 'Test Drive' },
  { value: 'FOLLOW_UP', label: 'Follow-up' },
  { value: 'OUTRO', label: 'Outro' },
];

export function TarefaForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const isEditing = !!id;

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [vendedores, setVendedores] = useState<Vendedor[]>([]);
  const [leads, setLeads] = useState<LeadOption[]>([]);
  const [veiculos, setVeiculos] = useState<VeiculoOption[]>([]);
  const [searchLead, setSearchLead] = useState('');
  const [searchVeiculo, setSearchVeiculo] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<TarefaFormData>({
    resolver: zodResolver(tarefaSchema),
    defaultValues: {
      prioridade: 'MEDIA',
      concluida: false,
      leadId: parseInt(searchParams.get('leadId') || '0') || null,
      veiculoId: parseInt(searchParams.get('veiculoId') || '0') || null,
      dataVencimento: getDefaultDate(),
    },
  });

  const concluida = watch('concluida');
  const selectedLeadId = watch('leadId');
  const selectedVeiculoId = watch('veiculoId');

  function getDefaultDate() {
    const date = new Date();
    date.setHours(date.getHours() + 1);
    return date.toISOString().slice(0, 16);
  }

  // Carregar dados auxiliares
  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      api.get<Vendedor[]>('/usuarios/vendedores', { signal: controller.signal }).catch(() => ({ data: [] })),
    ])
      .then(([vendedoresRes]) => {
        setVendedores(vendedoresRes.data);
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  // Buscar leads
  useEffect(() => {
    if (searchLead.length < 2) {
      setLeads([]);
      return;
    }
    const controller = new AbortController();
    const timeout = setTimeout(() => {
      api.get<{ items: LeadOption[] }>(`/leads?busca=${encodeURIComponent(searchLead)}&pageSize=10`, { signal: controller.signal })
        .then((res) => setLeads(res.data.items))
        .catch(() => {});
    }, 300);
    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [searchLead]);

  // Buscar veículos
  useEffect(() => {
    if (searchVeiculo.length < 2) {
      setVeiculos([]);
      return;
    }
    const controller = new AbortController();
    const timeout = setTimeout(() => {
      api.get<{ items: VeiculoOption[] }>(`/veiculos?busca=${encodeURIComponent(searchVeiculo)}&pageSize=10`, { signal: controller.signal })
        .then((res) => setVeiculos(res.data.items))
        .catch(() => {});
    }, 300);
    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [searchVeiculo]);

  // Carregar tarefa para edição
  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();
    setIsLoading(true);
    api.get<Record<string, unknown>>(`/tarefas/${id}`, { signal: controller.signal })
      .then((response) => {
        const tarefa = response.data;
        setValue('titulo', (tarefa.titulo as string) || '');
        setValue('descricao', (tarefa.descricao as string) || '');
        setValue('tipo', (tarefa.tipo as string) || '');
        setValue('prioridade', (tarefa.prioridade as TarefaFormData['prioridade']) || 'MEDIA');
        setValue('concluida', (tarefa.concluida as boolean) || false);
        setValue('usuarioId', (tarefa.usuarioId as number) || null);
        setValue('leadId', (tarefa.leadId as number) || null);
        setValue('veiculoId', (tarefa.veiculoId as number) || null);

        // Formatar data para input datetime-local
        if (tarefa.dataVencimento) {
          const date = new Date(tarefa.dataVencimento as string);
          setValue('dataVencimento', date.toISOString().slice(0, 16));
        }

        // Carregar nome do lead se existir
        if (tarefa.leadNome) {
          setSearchLead(tarefa.leadNome as string);
        }
        if (tarefa.veiculoDescricao) {
          setSearchVeiculo(tarefa.veiculoDescricao as string);
        }
      })
      .catch(() => showError('Erro ao carregar tarefa'))
      .finally(() => setIsLoading(false));
    return () => controller.abort();
  }, [id, setValue, showError]);

  const onSubmit = async (data: TarefaFormData) => {
    setIsSaving(true);
    try {
      const payload = {
        ...data,
        dataVencimento: new Date(data.dataVencimento).toISOString(),
      };

      if (isEditing) {
        await api.put(`/tarefas/${id}`, payload);
      } else {
        await api.post('/tarefas', payload);
      }

      showSuccess(isEditing ? 'Tarefa atualizada com sucesso' : 'Tarefa criada com sucesso');
      navigate('/crm/tarefas');
    } catch {
      showError('Erro ao salvar tarefa');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={isEditing ? 'Editar Tarefa' : 'Nova Tarefa'}
        subtitle={isEditing ? 'Altere os dados da tarefa' : 'Cadastre uma nova tarefa'}
        showBack
        backTo="/crm/tarefas"
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Dados da Tarefa */}
        <Panel title="Dados da Tarefa">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Título *
              </label>
              <input
                type="text"
                {...register('titulo')}
                placeholder="Ex: Ligar para cliente sobre financiamento"
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
              {errors.titulo && (
                <p className="mt-1 text-sm text-rose-600">{errors.titulo.message}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Descrição
              </label>
              <textarea
                {...register('descricao')}
                rows={3}
                placeholder="Detalhes adicionais sobre a tarefa..."
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Tipo
              </label>
              <select
                {...register('tipo')}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              >
                <option value="">Selecione...</option>
                {tiposOptions.map((tipo) => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Prioridade *
              </label>
              <select
                {...register('prioridade')}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              >
                <option value="BAIXA">Baixa</option>
                <option value="MEDIA">Média</option>
                <option value="ALTA">Alta</option>
                <option value="URGENTE">Urgente</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Data de Vencimento *
              </label>
              <input
                type="datetime-local"
                {...register('dataVencimento')}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
              {errors.dataVencimento && (
                <p className="mt-1 text-sm text-rose-600">{errors.dataVencimento.message}</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Responsável
              </label>
              <select
                {...register('usuarioId')}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              >
                <option value="">Selecione...</option>
                {vendedores.map((vendedor) => (
                  <option key={vendedor.id} value={vendedor.id}>
                    {vendedor.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Panel>

        {/* Vínculos */}
        <Panel title="Vínculos">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Lead
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchLead}
                  onChange={(e) => {
                    setSearchLead(e.target.value);
                    if (!e.target.value) setValue('leadId', null);
                  }}
                  placeholder="Buscar lead..."
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                />
                {leads.length > 0 && !selectedLeadId && (
                  <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-xl border border-slate-200/70 bg-white shadow-lg">
                    {leads.map((lead) => (
                      <button
                        key={lead.id}
                        type="button"
                        onClick={() => {
                          setValue('leadId', lead.id);
                          setSearchLead(lead.nome);
                          setLeads([]);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                      >
                        {lead.nome}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {selectedLeadId && (
                <button
                  type="button"
                  onClick={() => {
                    setValue('leadId', null);
                    setSearchLead('');
                  }}
                  className="mt-1 text-xs text-rose-600"
                >
                  Remover vínculo
                </button>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Veículo
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchVeiculo}
                  onChange={(e) => {
                    setSearchVeiculo(e.target.value);
                    if (!e.target.value) setValue('veiculoId', null);
                  }}
                  placeholder="Buscar veículo (placa ou modelo)..."
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                />
                {veiculos.length > 0 && !selectedVeiculoId && (
                  <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-xl border border-slate-200/70 bg-white shadow-lg">
                    {veiculos.map((veiculo) => (
                      <button
                        key={veiculo.id}
                        type="button"
                        onClick={() => {
                          setValue('veiculoId', veiculo.id);
                          setSearchVeiculo(`${veiculo.descricao} - ${veiculo.placa}`);
                          setVeiculos([]);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                      >
                        {veiculo.descricao} - {veiculo.placa}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {selectedVeiculoId && (
                <button
                  type="button"
                  onClick={() => {
                    setValue('veiculoId', null);
                    setSearchVeiculo('');
                  }}
                  className="mt-1 text-xs text-rose-600"
                >
                  Remover vínculo
                </button>
              )}
            </div>
          </div>
        </Panel>

        {/* Status (apenas em edição) */}
        {isEditing && (
          <Panel title="Status">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                {...register('concluida')}
                className="h-5 w-5 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
              />
              <span className={concluida ? 'font-semibold text-emerald-600' : 'text-slate-700'}>
                {concluida ? 'Tarefa concluída' : 'Marcar como concluída'}
              </span>
            </label>
          </Panel>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/crm/tarefas')}
            className={buttonClass('secondary')}
          >
            Cancelar
          </button>
          <Button type="submit" disabled={isSaving}>
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isSaving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </form>
    </div>
  );
}
